import { CSSProperties, useMemo } from "react";
import { AutoComplete, Select } from "antd";
import { BookingType } from "../../enums/BookingScope";
import {
  BOOKING_TYPE_OPTIONS,
  CUSTOM_BOOKING_TYPE,
  CUSTOM_TYPE_BASE_BOOKING_TYPE,
  CUSTOM_TYPE_MAX_LENGTH,
  getCustomTypeSuggestions,
} from "../../utils/customBookingType";

type CustomTypeInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
  className?: string;
  style?: CSSProperties;
};

/**
 * Campo di testo della tipologia personalizzata, con i suggerimenti delle
 * tipologie già usate. Compatibile con `Form.Item` (`value` / `onChange`).
 */
export function CustomTypeInput({
  value,
  onChange,
  autoFocus,
  className,
  style,
}: CustomTypeInputProps) {
  const options = useMemo(
    () => getCustomTypeSuggestions().map((label) => ({ value: label })),
    []
  );

  return (
    <AutoComplete
      value={value}
      onChange={onChange}
      options={options}
      filterOption={(input, option) =>
        (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
      }
      maxLength={CUSTOM_TYPE_MAX_LENGTH}
      autoFocus={autoFocus}
      className={className}
      style={style}
      placeholder="Es. Manutenzione"
      getPopupContainer={(trigger) => trigger.parentNode}
    />
  );
}

type Props = {
  bookingType?: BookingType;
  /**
   * `undefined` = tipo standard; stringa (anche vuota, se ancora da scrivere)
   * = tipologia personalizzata.
   */
  customType?: string;
  onChange: (bookingType: BookingType, customType?: string) => void;
  /** Mostra la voce "Personalizzata…" (solo prenotazioni della struttura). */
  allowCustom?: boolean;
};

/**
 * Selettore del tipo di prenotazione del gestionale: i tre tipi standard più
 * "Personalizzata…", che apre il campo per il nome della tipologia.
 */
export default function BookingTypeSelect({
  bookingType,
  customType,
  onChange,
  allowCustom = true,
}: Props) {
  const isCustom = customType !== undefined;
  const options = useMemo(
    () =>
      allowCustom
        ? [
            ...BOOKING_TYPE_OPTIONS,
            { label: "Personalizzata…", value: CUSTOM_BOOKING_TYPE },
          ]
        : BOOKING_TYPE_OPTIONS,
    [allowCustom]
  );

  return (
    <div className="d-flex flex-column gap-2 w-100">
      <Select
        options={options}
        className="w-100 p-0"
        placeholder="Scegli il tipo"
        value={isCustom ? CUSTOM_BOOKING_TYPE : bookingType}
        onSelect={(value: string) => {
          if (value === CUSTOM_BOOKING_TYPE) {
            if (!isCustom) onChange(CUSTOM_TYPE_BASE_BOOKING_TYPE, "");
          } else {
            onChange(value as BookingType, undefined);
          }
        }}
        getPopupContainer={(trigger) => trigger.parentNode}
      />
      {isCustom && (
        <CustomTypeInput
          autoFocus={customType === ""}
          className="w-100"
          value={customType}
          onChange={(value) => onChange(CUSTOM_TYPE_BASE_BOOKING_TYPE, value)}
        />
      )}
    </div>
  );
}
